import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import ScrollDepthTracker from "../components/ScrollDepthTracker";
import FadeInSection from "../components/FadeInSection";

export default function Media(props) {
  const mediaSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Media",
    "url": "https://www.thrombio.com.au/media",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    },
    "about": {
      "@type": "MedicalEntity",
      "name": "Thrombio Media",
      "description": "Thrombio's technology has received global attention for its breakthrough scientific studies."
    }
  };

  return (
    <>
      <SEO
        title="Media"
        description="ThromBio's technology has received global attention for its breakthrough scientific studies"
        keywords={['Media Coverage', 'News', 'Stroke Research', 'Medical Breakthroughs', 'Scientific Publications', 'Healthcare Innovation']}
        ogImage="/jpmhealthcareconferencefeb20251.webp"
        schema={mediaSchema}
      />
      <ScrollDepthTracker pageCategory="media" pageName="index" />

      <div className="pageContainer pt-44">
        {/* Optimized H1 tag with visible header preserving layout */}
        <FadeInSection>
          <h1 className="text-[4.8rem] font-bold leading-[5.2rem] tracking-[.192rem] pb-[15px]">
            <span className="sr-only">ThromBio Media: Latest News and Press Releases on Stroke Therapy</span>
            Media
          </h1>
        </FadeInSection>

        {/* Regular Media Tiles - 3 columns on desktop, 1 column on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          <FadeInSection className="delay-500">
            {/* Gordon Research Conference - July 2026 */}
            <Link to="/media/gordonresearch">
              <MediaCard
                day="5"
                month="JULY"
                year="2026"
                image="/grcmedia.png"
                altText="Gordon Research Conference"
                fixedHeight={true}
              >
                <div className="font-syne text-[2.1rem] font-normal leading-[2.4rem] text-center text-[#C3EEFD] my-4">Gordon Research Conference</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.2rem] text-center">
                  Founder & Director, Professor Shaun Jackson presented as a keynote speaker at a Gordon Research Conference
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection className="md:delay-1000">
            <Link to="/media/nbnnews">
              <MediaCard
                day="30"
                month="APRIL"
                year="2026"
                image="/nbn-video-poster.webp"
                altText="NBN News Article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.7rem] tracking-[.06rem] text-center">NBN News</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.2rem] text-center">
                  NSW Premier visits John Hunter Hospital to celebrate landmark stroke trial results
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection className="lg:delay-[1500ms]">
            {/* Newcastle Herald Article - April 2026 */}
            <Link to="/media/newcastleherald">
              <MediaCard
                day="29"
                month="APRIL"
                year="2026"
                image="/newcastleheraldpaper.png"
                altText="Newcastle Herald Article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.7rem] tracking-[.06rem] text-center">Newcastle Herald</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.2rem] text-center">
                  Newcastle at the centre of world-first stroke breakthrough as patients describe recovery as <span>'nothing short of miraculous’</span>
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection>
            {/* Nature Article - May 2025 */}
            <Link to="/media/nature">
              <MediaCard
                day="20"
                month="MAY"
                year="2025"
                image="/nature-journal-cover.png"
                design={1}
                altText="16 January 2025 The J.P. Morgan Healthcare Conference"
                fixedHeight={true}
              >
                <div className="text-[#F00000] font-syne text-[2.6rem] font-bold leading-[3.9rem] tracking-[.08rem] text-center">
                  Nature Journal
                </div>
                <div className="text-white text-center font-barlow text-[2.5rem] leading-[3rem] font-semibold">
                  Big breakthrough by ThromBio scientists published in the world's premier scientific journal, <span className="font-bold">Nature</span>
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection>
            {/* JP Morgan Healthcare Conference - January 2025 */}
            <Link to="/media/jpmorgan">
              <MediaCard
                day="16"
                month="JANUARY"
                year="2025"
                image="/jpmhealthcareconferencefeb20251.webp"
                altText="16 January 2025 The J.P. Morgan Healthcare Conference"
                fixedHeight={true}
              >
                <div className="font-syne text-[2.1rem] font-normal leading-[3.9rem] tracking-[.08rem] text-center text-[#C3EEFD]">
                  J.P. Morgan
                </div>
                <div className="text-[1.9rem] leading-[4rem] text-center text-[#C3EEFD]">
                  Healthcare Conference
                </div>
                <div className="text-[1.7rem] italic leading-[2.2rem] text-center">
                  ThromBio CEO Peter Bush attends the J.P. Morgan Healthcare Conference
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection>
            {/* BioSciences Fund - April 2024 */}
            <MediaCard
              day="17"
              month="APRIL"
              year="2024"
              image="/mediapagebiosciencesfundfinal2.webp"
              altText="17 April 2024 BioSciences Fund"
              fixedHeight={true}
            >
              <div className="font-syne text-[2.1rem] font-normal leading-[3.9rem] tracking-[.08rem] text-center text-[#C3EEFD]">
                BioSciences Fund
              </div>
              <div className="text-[1.7rem] italic leading-[2.2rem] text-center">
                The funding will support a Phase II clinical study,{" "}
                <span className="font-bold text-[#C3EEFD]">Co-STARS</span>, to determine the optimal clinical dose of
                TBO-309 and further commercial discussions with potential partners for drug combination, monotherapy
                and stent (device) collaborations
              </div>
            </MediaCard>
          </FadeInSection>

          <FadeInSection>
            {/* 9News - May 2024 */}
            <Link to="/media/ninenews">
              <MediaCard
                day="4"
                month="MAY"
                year="2024"
                image="/video-poster-9News.png"
                altText="4 May 2024 9News article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">9News</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3rem] text-center">
                  Aussie-made drug helped change life of stroke survivor, experts hopeful of international trial
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection>
            {/* 9News - July 2023 */}
            <a rel="noreferrer" href="https://www.9news.com.au/national/medical-news-health-stroke-anti-clotting-drug-trials-heart-research-institute/1a6c2caf-b5ad-4c8a-a02f-8d24c162c71c" target="_blank">
              <MediaCard
                day="18"
                month="JULY"
                year="2023"
                image="/frame601.webp"
                altText="18 July 2023 9News article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">9News</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.3rem] text-center">
                  A potentially life-saving drug developed to treat strokes
                </div>
              </MediaCard>
            </a>
          </FadeInSection>

          <FadeInSection>
            {/* ABC News - July 2023 */}
            <Link to="/media/abc">
              <MediaCard
                day="13"
                month="JULY"
                year="2023"
                image="/frame60.webp"
                altText="13 July 2023 ABC News article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">ABC News</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.3rem] text-center">
                  Breakthrough anti-clotting drug shows promise for treating strokes
                </div>
              </MediaCard>
            </Link>
          </FadeInSection>

          <FadeInSection>
            {/* Herald Sun - July 2023 */}
            <a rel="noreferrer" href="https://www.heraldsun.com.au/news/victoria/royal-melbourne-hospital-among-six-hospitals-to-offer-patients-clotbusting-medication-in-worldfirst-trial/news-story/fbec8154de28ec368ed2b02838276653" target="_blank">
              <MediaCard
                day="13"
                month="JULY"
                year="2023"
                image="/mediaarticleslayout3o1.webp"
                altText="13 July 2023 Herald Sun article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">Herald Sun</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.2rem] text-center">
                  Stroke 'eureka moment'
                </div>
                <div className="font-barlow text-[2.8rem] font-[300] leading-[3.3rem] text-center">
                  Human trials begin on clot-busting drug
                </div>
              </MediaCard>
            </a>
          </FadeInSection>

          <FadeInSection>
            {/* 3AW Melbourne - July 2023 */}
            <a rel="noreferrer" href="https://www.3aw.com.au/new-clot-busting-stroke-medication-to-be-trialled-in-australia/" target="_blank">
              <MediaCard
                day="13"
                month="JULY"
                year="2023"
                image="/mediaarticleslayout31.webp"
                altText="13 July 2023 3AW Melbourne radio article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">
                  3AW Melbourne
                </div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.3rem] text-center">
                  New clot-busting stroke drug to be trialled in Australia
                </div>
              </MediaCard>
            </a>
          </FadeInSection>

          <FadeInSection>
            {/* Daily Mail - July 2021 */}
            <a rel="noreferrer" href="https://www.hri.org.au/the-latest/news/global-media-interest-in-hris-world-first-stroke-drug-trial" target="_blank">
              <MediaCard
                day="18"
                month="JULY"
                year="2021"
                image="/screenshot20240325at5.08.25pm1.webp"
                altText="18 July 2021 Daily Mail article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">Daily Mail</div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.3rem] text-center">
                  Global media interest in HRI's world-first stroke drug trial
                </div>
              </MediaCard>
            </a>
          </FadeInSection>

          <FadeInSection>
            {/* Heart Research Institute - August 2019 */}
            <a rel="noreferrer" href="https://www.hri.org.au/news/hri-announces-stroke-scientific-breakthrough" target="_blank">
              <MediaCard
                day="26"
                month="AUGUST"
                year="2019"
                image="/frame602.webp"
                altText="26 August 2019 Heart Research Institute article"
                fixedHeight={true}
              >
                <div className="font-syne text-[2rem] leading-[3.9rem] tracking-[.06rem] text-center">
                  Heart Research Institute
                </div>
                <div className="font-barlow text-[2.8rem] font-semibold leading-[3.3rem] text-center">
                  Stroke scientific breakthrough - HRI Thrombosis Group
                </div>
              </MediaCard>
            </a>
          </FadeInSection>
        </div>
      </div >
    </>
  );
}

function MediaCard(props) {
  const {
    image,
    day,
    month,
    year,
    altText,
    design = 0,
    children,
    featured = false,
    fixedHeight = false
  } = props;

  // Create different border styles based on design prop
  let borderStyle = "border-[#C3EEFD]";
  if (design === 1) {
    // For the Nature tile (design 1), use a gradient
    borderStyle = "border-transparent bg-gradient-to-b from-[#00161E] to-[#B00000] p-[7px]";
  }

  return (
    <div className={`rounded-[40px] overflow-clip w-full ${fixedHeight ? (featured ? 'h-[540px]' : 'h-[511px]') : 'h-full'
      } ${design === 1 ? borderStyle : `border-[7px] ${borderStyle}`}`}>
      {/* For gradient border, we need an inner container */}
      <div className={`${design === 1 ? 'bg-[#03181F] h-full rounded-[33px] overflow-hidden' : ''} flex flex-col h-full`}>
        {day && month && year && (
          <div className="pt-[20px] flex flex-col items-center">
            <div className="text-center font-barlow text-[3rem] font-semibold leading-[3.5rem] tracking-[.03rem] mb-4">
              {day} {month}
            </div>
            <div className="font-barlow text-[6rem] font-semibold leading-[5rem] tracking-[-.12rem] text-center mb-4">
              {year}
            </div>
          </div>
        )}

        {children && (
          <div className="px-6 mb-8">
            {children}
          </div>
        )}

        <div className="mt-auto overflow-hidden">
          <img src={image} alt={altText || "Media image"} className="w-full object-cover" />
        </div>
      </div>
    </div>
  );
}